/**
 * AddKeyDateModal — create, edit, or apply-to-more-milestones for a project Key Date
 * (docs/KEY_DATES.md). One component, three modes, per the ElevateToThreadModal precedent:
 *
 *   mode="create" — label + date + notes, with a milestone checklist (this milestone pre-
 *                   checked and locked, others optional) so a brand-new key date can be shared
 *                   across several milestones from the moment it's created.
 *   mode="edit"   — label + date + notes only. Since a key date is ONE project-level row, this
 *                   save is exactly what makes it "shared" — every milestone already linked
 *                   picks up the change with no extra propagation step.
 *   mode="apply"  — milestone checklist only, for retroactively linking an existing key date to
 *                   more milestones. Already-linked milestones show checked and locked.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { getProjectMilestones } from '../services/projects.js'
import { createKeyDate, updateKeyDate, linkKeyDateToMilestones } from '../services/keyDates.js'

// Deliberately NOT services/passengers.js#getCandidateMilestones — that list is scoped to
// "could a thread ride this as a passenger" (Active behavior, unposted only), which would hide
// Fixed-behavior milestones like "Public Hearing 🔹" from this picker entirely. A key date is
// informational, not a work assignment, so every milestone on the project is a fair pick.
function toCandidate(r) {
  const f = r.fields || {}
  return {
    milestoneId: r.id,
    name: f['Name (from Deliverable)']?.[0] || f['Subtitle'] || f['Name'] || 'Untitled milestone',
    phaseName: f['Project Phase'] || '',
  }
}

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

export default function AddKeyDateModal({
  mode = 'create', projectId, currentMilestoneId, existing, memberId, memberName, onClose, onSaved,
}) {
  const [label, setLabel] = useState(existing?.label || '')
  const [date,  setDate]  = useState(existing?.date || '')
  const [notes, setNotes] = useState(existing?.notes || '')
  const [candidates, setCandidates] = useState(null)
  const [checked, setChecked] = useState(() => {
    const s = new Set(existing?.milestoneIds || [])
    if (mode === 'create' && currentMilestoneId) s.add(currentMilestoneId)
    return s
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const showFields = mode !== 'apply'
  const showMilestones = mode !== 'edit'

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    if (showMilestones) {
      getProjectMilestones(projectId)
        .then(records => setCandidates((records || []).map(toCandidate)))
        .catch(() => setCandidates([]))
    }
    return () => { document.body.style.overflow = '' }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  function toggleMs(id) {
    if (mode === 'apply' && (existing?.milestoneIds || []).includes(id)) return // already-linked, locked
    if (mode === 'create' && id === currentMilestoneId) return // this milestone, locked
    setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  async function handleSubmit() {
    if (submitting) return
    if (showFields && !label.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      if (mode === 'create') {
        const kd = await createKeyDate(projectId, {
          label: label.trim(), date: date || null, notes: notes.trim() || undefined,
          milestoneIds: [...checked], memberId, memberName,
        })
        onSaved(kd)
      } else if (mode === 'edit') {
        const kd = await updateKeyDate(existing.id, {
          label: label.trim(), date: date || null, notes: notes.trim(), memberId, memberName,
        })
        onSaved(kd)
      } else {
        const kd = await linkKeyDateToMilestones(existing.id, [...checked])
        onSaved(kd)
      }
    } catch (e) {
      setError(e.message || 'Save failed')
      setSubmitting(false)
    }
  }

  const canSubmit = !submitting && (!showFields || label.trim())
  const title = mode === 'create' ? 'Add Key Date' : mode === 'edit' ? 'Edit Key Date' : 'Apply to More Milestones'
  const submitLabel = mode === 'create' ? 'Add Key Date' : mode === 'edit' ? 'Save' : 'Apply'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="AddKeyDateModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        {mode === 'create' && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginBottom: 16, lineHeight: 1.4 }}>
            A calendar flag for the crew — not a milestone, no work attached. Pick every milestone that should see it; editing it later from any one of them updates all of them.
          </div>
        )}
        {mode === 'apply' && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginBottom: 16, lineHeight: 1.4 }}>
            "{existing?.label}" will show under every milestone checked below, in addition to where it already shows.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {showFields && (
            <>
              <div>
                <label style={labelStyle}>Label <span style={{ color: '#c0392b' }}>*</span></label>
                <input type="text" style={inputStyle} value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Scheduled Hearing Date" autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Date</label>
                <input type="date" style={inputStyle} value={date || ''} onChange={e => setDate(e.target.value)} />
                <div style={{ marginTop: 5, fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)' }}>
                  Leave blank if it isn't scheduled yet — it'll still show as a note without a diamond on the Gantt.
                </div>
              </div>
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional context…" />
              </div>
            </>
          )}

          {showMilestones && (
            <div>
              <label style={labelStyle}>{mode === 'create' ? 'Also applies to' : 'Apply to'}</label>
              <div style={{ maxHeight: 180, overflowY: 'auto', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)' }}>
                {candidates === null && (
                  <div style={{ padding: '10px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' }}>Loading milestones…</div>
                )}
                {Array.isArray(candidates) && candidates.length === 0 && (
                  <div style={{ padding: '10px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' }}>No open milestones on this project.</div>
                )}
                {Array.isArray(candidates) && candidates.map(m => {
                  const isChecked = checked.has(m.milestoneId)
                  const locked = (mode === 'create' && m.milestoneId === currentMilestoneId)
                    || (mode === 'apply' && (existing?.milestoneIds || []).includes(m.milestoneId))
                  return (
                    <label key={m.milestoneId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: locked ? 'var(--aj-text-muted)' : 'var(--aj-text)', cursor: locked ? 'default' : 'pointer', borderBottom: '0.5px solid var(--aj-border)' }}>
                      <input type="checkbox" checked={isChecked} disabled={locked} onChange={() => toggleMs(m.milestoneId)} />
                      <span style={{ flex: 1 }}>{m.name}{m.phaseName ? '  ·  ' + m.phaseName : ''}{locked ? '  ·  already linked' : ''}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-mid)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default', transition: 'background 0.12s' }}
            >
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
